import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  AdminSendRecord,
  CommsProfileResponse,
  DeliveryHistoryParams,
  DeliveryHistoryResponse,
  NotificationTemplate,
  NotificationType,
  NotificationTypeListParams,
  NotificationTypesListResponse,
  RecipientRef,
  SendCheckResult,
  SendListParams,
  SendsListResponse,
  SuppressionListParams,
  SuppressionsListResponse,
  TemplateListParams,
  TemplatesListResponse,
  UserPreferencesResponse,
} from '@/client/types/campaigns';

const MSG = '/api/admin/v2/messaging';
const USERS = '/api/admin/v2/users';

export const campaignsKeys = {
  all: ['campaigns-messaging-v2'] as const,
  sends: () => [...campaignsKeys.all, 'sends'] as const,
  sendList: (p: object) => [...campaignsKeys.sends(), 'list', p] as const,
  sendDetail: (msgid: string) => [...campaignsKeys.sends(), 'detail', msgid] as const,
  templates: () => [...campaignsKeys.all, 'templates'] as const,
  templateList: (p: object) => [...campaignsKeys.templates(), 'list', p] as const,
  templateDetail: (id: number) => [...campaignsKeys.templates(), 'detail', id] as const,
  suppressions: () => [...campaignsKeys.all, 'suppressions'] as const,
  suppressionList: (p: object) => [...campaignsKeys.suppressions(), 'list', p] as const,
  notifTypes: () => [...campaignsKeys.all, 'notification-types'] as const,
  notifTypeList: (p: object) => [...campaignsKeys.notifTypes(), 'list', p] as const,
  user: (uid: number) => [...campaignsKeys.all, 'user', uid] as const,
  commsProfile: (uid: number) => [...campaignsKeys.user(uid), 'comms-profile'] as const,
  preferences: (uid: number) => [...campaignsKeys.user(uid), 'preferences'] as const,
  deliveryHistory: (uid: number, p: object) =>
    [...campaignsKeys.user(uid), 'delivery-history', p] as const,
};

const qs = (raw: Record<string, unknown>) => {
  const params = Object.entries(raw).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const s = new URLSearchParams(params).toString();
  return s ? `?${s}` : '';
};

/** Messaging v2 wraps every payload in `{ status, data }` — always read `.data`. */
/** Opt-out status of each recipient for `channels`, re-checked as the list changes. */
export const useSendCheckQuery = (recipients: RecipientRef[], channels: string[]) => {
  const request = useClientStore((s) => s.request);
  const body = { recipients, channels };
  return useQuery({
    queryKey: [...campaignsKeys.sends(), 'check', body],
    queryFn: withErrorHandling(
      async (): Promise<SendCheckResult> => unwrap(request<APIResponse<SendCheckResult>>(`${MSG}/send/check`, 'POST', body)),
      'Failed to check opt-outs',
    ),
    enabled: recipients.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
};

const unwrap = async <T>(promise: Promise<APIResponse<T>>): Promise<T> => {
  const res = await promise;
  return res.data as T;
};

// ─── Sends ────────────────────────────────────────────────────────────────────

export const useSendListQuery = (params: SendListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.sendList(params),
    queryFn: withErrorHandling(async (): Promise<SendsListResponse> => {
      return unwrap(
        request<APIResponse<SendsListResponse>>(
          `${MSG}/sends${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch send history'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSendDetailQuery = (msgid: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.sendDetail(msgid ?? ''),
    queryFn: withErrorHandling(async (): Promise<AdminSendRecord> => {
      return unwrap(
        request<APIResponse<AdminSendRecord>>(`${MSG}/sends/${encodeURIComponent(msgid!)}`),
      );
    }, 'Failed to fetch send'),
    enabled: enabled && !!msgid,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Templates ────────────────────────────────────────────────────────────────

export const useTemplateListQuery = (params: TemplateListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.templateList(params),
    queryFn: withErrorHandling(async (): Promise<TemplatesListResponse> => {
      return unwrap(
        request<APIResponse<TemplatesListResponse>>(
          `${MSG}/templates${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch templates'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useTemplateDetailQuery = (ntmplid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.templateDetail(ntmplid ?? 0),
    queryFn: withErrorHandling(async (): Promise<NotificationTemplate> => {
      const raw = await unwrap(
        request<APIResponse<NotificationTemplate & { template?: NotificationTemplate }>>(
          `${MSG}/templates/${ntmplid}`,
        ),
      );
      return raw.template ?? raw;
    }, 'Failed to fetch template'),
    enabled: enabled && ntmplid != null && ntmplid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Suppressions ─────────────────────────────────────────────────────────────

export const useSuppressionListQuery = (
  params: SuppressionListParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.suppressionList(params),
    queryFn: withErrorHandling(async (): Promise<SuppressionsListResponse> => {
      return unwrap(
        request<APIResponse<SuppressionsListResponse>>(
          `${MSG}/suppressions${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch SMS suppressions'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Notification types ───────────────────────────────────────────────────────

export const useNotificationTypeListQuery = (
  params: NotificationTypeListParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.notifTypeList(params),
    queryFn: withErrorHandling(async (): Promise<NotificationTypesListResponse> => {
      return unwrap(
        request<APIResponse<NotificationTypesListResponse>>(
          `${MSG}/notification-types${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch notification types'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const normalizeNotificationTypes = (
  data?: NotificationTypesListResponse | null,
): NotificationType[] => data?.notification_types ?? data?.types ?? [];

// ─── Per-user comms ───────────────────────────────────────────────────────────

export const useCommsProfileQuery = (uid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.commsProfile(uid ?? 0),
    queryFn: withErrorHandling(async (): Promise<CommsProfileResponse> => {
      return unwrap(
        request<APIResponse<CommsProfileResponse>>(`${USERS}/${uid}/comms-profile`),
      );
    }, 'Failed to fetch comms profile'),
    enabled: enabled && uid != null && uid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useUserPreferencesQuery = (uid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.preferences(uid ?? 0),
    queryFn: withErrorHandling(async (): Promise<UserPreferencesResponse> => {
      return unwrap(
        request<APIResponse<UserPreferencesResponse>>(
          `${USERS}/${uid}/notification-preferences`,
        ),
      );
    }, 'Failed to fetch notification preferences'),
    enabled: enabled && uid != null && uid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useDeliveryHistoryQuery = (
  uid: number | null,
  params: DeliveryHistoryParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: campaignsKeys.deliveryHistory(uid ?? 0, params),
    queryFn: withErrorHandling(async (): Promise<DeliveryHistoryResponse> => {
      return unwrap(
        request<APIResponse<DeliveryHistoryResponse>>(
          `${USERS}/${uid}/delivery-history${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch delivery history'),
    enabled: enabled && uid != null && uid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
